import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import type { PicksResponse } from '@lifetimeline/shared';

import { Button } from '../components/Button.js';
import { Layout } from '../components/Layout.js';
import { Notice } from '../components/Notice.js';
import { Sheet } from '../components/Sheet.js';
import { Spinner } from '../components/Spinner.js';
import { useToast } from '../components/Toast.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { renderGoogleButton } from '../features/auth/googleSignIn.js';
import { MergeSheet } from '../features/merge/MergeSheet.js';
import {
  buildMergePlan,
  isNoop,
  needsUserDecision,
  resolveMergePlan,
  type MergePlan,
} from '../features/merge/mergePlan.js';
import { useI18n } from '../i18n/index.js';
import { api, errorMessageKey } from '../lib/apiClient.js';
import { loadCatalog, type LoadedCatalog } from '../lib/catalog.js';
import { clearLocalPicks, readLocalPicks, type LocalPick } from '../lib/localPicks.js';
import { clearLocalImages } from '../lib/imageStore.js';
import { uploadLocalPicks } from '../features/merge/transferLocalPicks.js';
import styles from './pageStyles.module.css';

/** 개발용 로그인을 보여줄 조건. 프로덕션 번들에서는 항상 false다. */
const DEV_AUTH_AVAILABLE = import.meta.env.DEV;

/**
 * 개발용 계정 **둘**.
 *
 * 인연은 한쪽이 요청하고 **상대가 수락해야** 맺어진다. 계정이 하나면 그
 * 절반을 영영 못 본다 — 요청을 보내 놓고 수락하는 화면을 열 수가 없다.
 *
 * 서버는 `dev:<아무 문자열>`을 그 문자열의 구글 sub로 읽는다. 처음 누르면
 * 계정이 없으므로 가입 화면으로 가고, 그다음부터는 그 계정으로 들어온다.
 */
const DEV_ACCOUNTS = ['junho', 'mina'] as const;

/**
 * 로그인.
 *
 * 로그인 자체보다 **그다음 분기**가 중요하다:
 *   계정 없음 → 가입 화면 (로컬 목록은 가입 후 그대로 올린다)
 *   계정 있음 → 로컬 목록과 병합 판정
 */
export function SignInPage() {
  const { t, language } = useI18n();
  const { status, signIn } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<MergePlan | null>(null);
  const [catalog, setCatalog] = useState<LoadedCatalog | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadCatalog(language)
      .then((loaded) => {
        if (!cancelled) {
          setCatalog(loaded);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [language]);

  /**
   * 로컬 목록을 계정에 반영한다. 성공한 뒤에만 로컬을 비운다.
   *
   * 사진이 브라우저 안의 Blob이라 **올리는 게 먼저다.** 실제로 저장할 것만
   * 올린다 — 사용자가 "계정 것을 유지"로 끝낸 Pick의 사진까지 올리면
   * 아무도 안 쓰는 파일이 버킷에 남는다.
   */
  const applyPicks = useCallback(async (picks: readonly LocalPick[]) => {
    if (picks.length > 0) {
      await api.put('/api/me/picks', { picks: await uploadLocalPicks(picks) });
    }
    // ⚠️ 순서가 중요하다. 먼저 비우면 서버 저장이 실패했을 때 데이터가 증발한다.
    clearLocalPicks();
    void clearLocalImages();
  }, []);

  const handleSignIn = useCallback(
    async (idToken: string) => {
      setBusy(true);
      setError(null);
      try {
        const response = await signIn(idToken);

        if (response.user === null) {
          // 신규 — 가입 화면에서 userId를 정한 뒤 목록을 올린다
          navigate('/signup', { replace: true });
          return;
        }

        const localPicks = readLocalPicks();
        if (localPicks.length === 0) {
          navigate('/me', { replace: true });
          return;
        }

        // 계정에 이미 있는 것과 대조한다
        const serverPicks = await api.get<PicksResponse>('/api/me/picks');
        const nextPlan = buildMergePlan(localPicks, serverPicks.picks);

        if (isNoop(nextPlan)) {
          clearLocalPicks();
          void clearLocalImages();
          navigate('/me', { replace: true });
          return;
        }

        if (!needsUserDecision(nextPlan)) {
          // 겹치는 게 없으면 물어볼 것도 없다 — 바로 합친다
          await applyPicks(resolveMergePlan(nextPlan, new Map()));
          toast.show(t('merge.done'));
          navigate('/me', { replace: true });
          return;
        }

        setPlan(nextPlan);
      } catch (cause) {
        setError(t(errorMessageKey(cause)));
      } finally {
        setBusy(false);
      }
    },
    [signIn, navigate, applyPicks, toast, t],
  );

  /*
   * 이미 로그인돼 있으면 이 화면에 머물 이유가 없다.
   *
   * ⚠️ `busy`를 함께 보는 것이 핵심이다. `signIn()`이 성공하는 순간 상태가
   * `authenticated`로 바뀌는데, 병합 판정은 그 뒤에 이어지는 **비동기 작업**이다.
   * busy를 안 보면 판정이 끝나기 전에 여기서 화면을 떠나버리고, 그러면
   * `setPlan`이 이미 사라진 컴포넌트에 걸려 **충돌 선택 화면이 영영 안 뜬다** —
   * 비로그인 목록이 조용히 이관되지 않은 채 남는다(실제로 그랬다).
   */
  if (status === 'authenticated' && plan === null && !busy) {
    return <Navigate to="/me" replace />;
  }

  return (
    <Layout narrow>
      <div className={styles.head}>
        <h1 className={styles.title}>{t('auth.signInTitle')}</h1>
        <p className={styles.subtitle}>{t('auth.signInLead')}</p>
      </div>

      {error !== null ? (
        <div className={styles.noticeSlot}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : null}

      {busy ? (
        <div className={styles.centered}>
          <Spinner label={t('auth.signingIn')} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s3)' }}>
          <GoogleSignInButton onToken={(token) => void handleSignIn(token)} onError={setError} />

          {DEV_AUTH_AVAILABLE ? (
            <>
              {DEV_ACCOUNTS.map((name) => (
                <Button
                  key={name}
                  variant="outline"
                  fullWidth
                  size="lg"
                  onClick={() => void handleSignIn(`dev:${name}`)}
                >
                  {t('auth.devSignInAs', { name })}
                </Button>
              ))}
              <p style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', textAlign: 'center' }}>
                {t('auth.devSignInHint')}
              </p>
            </>
          ) : null}
        </div>
      )}

      <Sheet open={plan !== null} onClose={() => setPlan(null)} title={t('merge.title')}>
        {plan !== null ? (
          <MergeSheet
            plan={plan}
            catalog={catalog}
            submitting={applying}
            onApply={(picks) => {
              setApplying(true);
              applyPicks(picks)
                .then(() => {
                  toast.show(t('merge.done'));
                  navigate('/me', { replace: true });
                })
                .catch((cause: unknown) => {
                  toast.show(t(errorMessageKey(cause)), 'error');
                })
                .finally(() => setApplying(false));
            }}
            onSkip={() => {
              // 로컬을 버리고 계정 것을 그대로 쓴다.
              // 되돌릴 수 없으므로 사용자가 명시적으로 고른 경우에만 온다.
              clearLocalPicks();
              navigate('/me', { replace: true });
            }}
          />
        ) : null}
      </Sheet>
    </Layout>
  );
}

/**
 * 구글 로그인 버튼.
 *
 * `VITE_GOOGLE_CLIENT_ID`가 설정되면 구글이 그리는 공식 버튼이 뜨고,
 * 없으면 안내만 보여준다 — **반쪽짜리 버튼을 두지 않는다.** 눌렀는데 아무 일도
 * 안 일어나거나 알 수 없는 오류가 나는 것이 가장 나쁘다.
 */
function GoogleSignInButton({
  onToken,
  onError,
}: {
  onToken: (idToken: string) => void;
  onError: (message: string) => void;
}) {
  const { t, language } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const clientId = import.meta.env['VITE_GOOGLE_CLIENT_ID'] as string | undefined;
  const configured = clientId !== undefined && clientId !== '';

  /*
   * 콜백은 ref에 담아 둔다.
   *
   * 의존성에 그대로 넣으면 매 렌더 새 함수라 effect가 계속 다시 돌고, 그때마다
   * 구글 버튼이 통째로 갈아끼워진다 — 누르는 순간 눌린 요소가 사라져 클릭이
   * 먹지 않는 일이 생긴다. 다시 그려야 할 이유는 **언어가 바뀔 때**뿐이다.
   */
  const callbacks = useRef({ onToken, onError, t });
  callbacks.current = { onToken, onError, t };

  useEffect(() => {
    const container = containerRef.current;
    if (!configured || container === null || clientId === undefined) {
      return;
    }
    renderGoogleButton({
      clientId,
      container,
      locale: language,
      onCredential: (idToken) => callbacks.current.onToken(idToken),
      onError: () => callbacks.current.onError(callbacks.current.t('error.network')),
    });
  }, [configured, clientId, language]);

  if (!configured) {
    return (
      <Notice tone="info">
        {t('auth.google')} — VITE_GOOGLE_CLIENT_ID가 아직 설정되지 않았습니다.
      </Notice>
    );
  }

  // 구글이 이 자리에 자기 버튼을 그린다
  return <div ref={containerRef} className={styles.googleSlot} />;
}
